import { ProfileHoverCard } from "@circle-react/components/Profile/HoverCard";
import { REACTION_PRESENTATION } from "@circle-react/components/Reactions/PostCommentReactionAction/constants";
import { isIntendedOnNewTab } from "@circle-react/helpers/browserHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useProfileLinkProps } from "@circle-react/hooks/profile/useProfileLinkProps";
import type { PostLikeMember } from "@circle-react/lib/queries/posts";
import { Avatar } from "@circleco/compass/components/Avatar";
import type { AvatarSentimentSize } from "@circleco/compass/components/Avatar";

import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";

interface MemberRowProps {
  avatarSize?: AvatarSentimentSize;
  className?: string;
  member: PostLikeMember;
  onNavigate: () => void;
  showHeadline?: boolean;
  showReactionBadge?: boolean;
}

export const MemberRow = ({
  member,
  avatarSize = "md",
  className,
  onNavigate,
  showHeadline = false,
  showReactionBadge = false,
}: MemberRowProps) => {
  const { onClick, href, ...profileLinkProps } = useProfileLinkProps({
    public_uid: member.public_uid,
  });
  const sentiment =
    showReactionBadge && member.reaction_type
      ? REACTION_PRESENTATION[member.reaction_type].sentiment
      : null;

  return (
    <a
      className={classNames("flex items-center gap-3", className)}
      href={href}
      onClick={event => {
        if (!isIntendedOnNewTab(event)) {
          onNavigate();
        }
        onClick?.(event);
      }}
      {...profileLinkProps}
    >
      <div className="shrink-0">
        <ProfileHoverCard
          communityMemberId={member.id}
          name={member.name}
          publicUid={member.public_uid}
          headline={member.headline}
          avatarURL={member.avatar_url}
        >
          {sentiment ? (
            <Avatar
              name={member.name}
              src={member.avatar_url}
              size={avatarSize}
              sentiment={sentiment}
            />
          ) : (
            <Avatar
              name={member.name}
              src={member.avatar_url}
              size={avatarSize}
            />
          )}
        </ProfileHoverCard>
      </div>
      {showHeadline ? (
        <div
          className={classNames(
            "flex min-h-10 min-w-0 flex-1 flex-col",
            !member.headline && "justify-center",
          )}
        >
          <Typography.LabelSm weight="medium" truncate>
            {member.name}
          </Typography.LabelSm>
          {member.headline && (
            <Typography.BodySm color="text-default" truncate>
              {member.headline}
            </Typography.BodySm>
          )}
        </div>
      ) : (
        <Typography.LabelSm weight="medium" truncate>
          {member.name}
        </Typography.LabelSm>
      )}
    </a>
  );
};
